import { createAsyncThunk } from "@reduxjs/toolkit";
import { WaterCharacteristicsPreference } from "@src/core/api/waterCharacteristicsPreferences";
import { BatchSelectionResponse } from "@src/core/engine/workers/batchSelection/schema";
import { RootState } from "@src/core/store";
import { BuildSuggestionOutput } from "@src/routes/projects/$projectId/selections/hooks/useBuildSuggestionOutput";
import { GetGroupedProductDescriptions } from "@src/shared/components/ProductDetailsCard/hooks/useGetProductDescriptions";
import { UnitConversionFn } from "@src/shared/hooks/useUnitConversion";
import { set } from "radashi";
import { FetchSuggestions } from "../contexts/BatchSuggestionsContext";
import { BatchSelectionState } from "./batchSelectionSlice";
import { buildInput } from "./helpers/buildInput";
import { ensureDataState } from "./helpers/ensureDataState";
import { matchesRequest } from "./helpers/matchesRequest";
import { processRowUpdate } from "./helpers/processRowUpdate";
import { BatchSelectionRow } from "./types";

type UpdateCellValueArgs = {
  buildSuggestionOutput: BuildSuggestionOutput;
  categoryId: string;
  columnId: keyof BatchSelectionRow;
  convertToInternationalUnit: UnitConversionFn;
  convertToUserUnit: UnitConversionFn;
  fetchSuggestions: FetchSuggestions;
  getProductDescriptions: GetGroupedProductDescriptions;
  index: number;
  useFlowOrPower: string;
  value: number | string;
  waitUntilReady: () => Promise<void>;
  waterCharacteristics: WaterCharacteristicsPreference | null;
};

export const initUpdateCellValue = (
  state: BatchSelectionState,
  args: UpdateCellValueArgs,
  requestId: string,
) => {
  const { columnId, index, value } = args;
  const nextData = set(state.data, `${index}.${columnId}`, value);
  const cleanData = ensureDataState(nextData);
  cleanData[index] = processRowUpdate({
    correlationId: requestId,
    prevRow: state.data.find((row) => row.id === cleanData[index].id),
    row: cleanData[index],
    ...args,
  });
  state.data = cleanData;
};

export const updateCellValue = createAsyncThunk<
  Omit<BatchSelectionResponse, "type">,
  UpdateCellValueArgs,
  { state: RootState }
>(
  "batchSelection/updateCellValue",
  async (
    {
      categoryId,
      convertToInternationalUnit,
      fetchSuggestions,
      index,
      waitUntilReady,
    },
    { getState, requestId },
  ) => {
    await waitUntilReady();
    const item = getState().batchSelection.data.at(index);
    if (!item || !matchesRequest(item, requestId)) {
      return { correlationId: requestId, results: [] };
    }

    return fetchSuggestions({
      categoryId,
      correlationId: requestId,
      inputs: [buildInput(item, convertToInternationalUnit)],
    });
  },
);
